// Single door to an app's own API: same-origin cookie auth, antiforgery header on unsafe methods, errors as ApiError.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Field errors from a ValidationProblem, keyed by field (`email`, `emails[3]`), for forms that point at the field. */
    public readonly errors: Record<string, string[]> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const XSRF_COOKIE = "XSRF-TOKEN";
const XSRF_HEADER = "X-XSRF-TOKEN";

function readCookie(name: string): string | undefined {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

async function fetchXsrfToken(): Promise<void> {
  await fetch("/api/antiforgery/token", { credentials: "same-origin" });
}

export type RequestOptions = Omit<RequestInit, "body"> & {
  /** Sent as JSON. */
  json?: unknown;
  /** Sent as multipart/form-data (file uploads); the browser sets the boundary. */
  form?: FormData;
};

const SESSION_HEADER = "X-Ghezaa-Session";
const sessionListeners = new Set<() => void>();
let sessionChangedAt = 0;

/**
 * Called when the server says this request's refresh changed what the person may do (C6: a role, their status, language
 * or viewing as a role changed in Core). Parallel requests may all say so; listeners hear it once every few seconds.
 * Returns the unsubscribe.
 */
export function onSessionChanged(listener: () => void): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

function noticeSessionChange(response: Response) {
  if (response.headers.get(SESSION_HEADER) !== "changed") return;
  const now = Date.now();
  if (now - sessionChangedAt < 3000) return;
  sessionChangedAt = now;
  sessionListeners.forEach((listener) => listener());
}

export async function api<T>(path: string, options: RequestOptions = {}, retried = false): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const unsafe = method !== "GET" && method !== "HEAD";
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");

  let body: BodyInit | undefined;
  if (options.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(options.json);
  } else if (options.form) {
    body = options.form;
  }

  if (unsafe) {
    if (!readCookie(XSRF_COOKIE)) await fetchXsrfToken();
    const token = readCookie(XSRF_COOKIE);
    if (token) headers.set(XSRF_HEADER, decodeURIComponent(token));
  }

  const response = await fetch(path, { ...options, method, headers, body, credentials: "same-origin" });
  noticeSessionChange(response);

  // Antiforgery tokens are bound to the signed-in user; one issued before sign-in is stale. Refresh once and retry.
  if (unsafe && response.status === 400 && !retried) {
    const problem = await response.clone().json().catch(() => null);
    if (problem?.title?.includes("antiforgery")) {
      await fetchXsrfToken();
      return api<T>(path, options, true);
    }
  }

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    let message = response.statusText || `Request failed (${response.status})`;
    let errors: Record<string, string[]> = {};
    try {
      const problem = await response.json();
      // ValidationProblem carries field errors; surface them instead of the generic title.
      if (problem?.errors && typeof problem.errors === "object") errors = problem.errors as Record<string, string[]>;
      const fieldErrors = Object.values(errors).flat();
      message = fieldErrors.length > 0 ? fieldErrors.join(" ") : (problem.detail ?? problem.title ?? message);
    } catch {
      // body was not JSON
    }
    throw new ApiError(response.status, message, errors);
  }

  // 202 Accepted (a queued job) and the like may carry no body.
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
